/**
 * Sentinel Conversations Hooks
 * Jotai atoms + hooks for the Sentinel review page (list, detail, submit).
 */

import type { SentinelConversationsResponse } from "@/services/sentinelReview.service";
import { buScopeKey, buScopeParams } from "@/lib/business-unit-scope";
import { useAtom } from "jotai";
import { atomWithInfiniteQuery, atomWithQuery } from "jotai-tanstack-query";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  sentinelReviewService,
  type SentinelConversationThread,
  type SentinelConversationListItem,
  type SentinelListFilters,
  type SentinelReviewsFilters,
  type SentinelReviewsResponse,
  type SubmitSentinelReviewInput,
  type EditSentinelReviewInput,
} from "@/services/sentinelReview.service";

const PAGE_SIZE = 20;
const STALE_TIME_MS = 60 * 1000;

/**
 * Infinite query atom for the Sentinel conversation list.
 */
const createSentinelConversationsAtom = (filters?: SentinelListFilters) => {
  const filterKey = filters
    ? JSON.stringify({
        contactSearch: filters.contactSearch,
        startDate: filters.startDate?.toISOString(),
        endDate: filters.endDate?.toISOString(),
        sortOrder: filters.sortOrder,
        reviewStatus: filters.reviewStatus,
      })
    : "no-filters";

  return atomWithInfiniteQuery((get) => ({
    // The brand belongs in the KEY as well as the request. A brand switch does not clear
    // queries, so a key without it serves the previous brand's conversations.
    queryKey: ["sentinel", "conversations", filterKey, buScopeKey(get)],
    queryFn: async ({ pageParam }: { pageParam?: number }) => {
      return sentinelReviewService.getSentinelConversations(PAGE_SIZE, pageParam, filters, buScopeParams(get));
    },
    // Annotated because taking `get` broke the inference chain that used to type this from
    // queryFn's return — without it `lastPage` widens to unknown and the build fails.
    getNextPageParam: (lastPage: SentinelConversationsResponse) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as number | undefined,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: STALE_TIME_MS,
  }));
};

/**
 * Hook: paginated list of conversations with pending Sentinel responses.
 */
export function useSentinelConversations(filters?: SentinelListFilters) {
  const contactSearch = filters?.contactSearch;
  const startDateISO = filters?.startDate?.toISOString();
  const endDateISO = filters?.endDate?.toISOString();
  const sortOrder = filters?.sortOrder;
  const reviewStatus = filters?.reviewStatus;

  const queryAtom = useMemo(
    () => createSentinelConversationsAtom(filters),
    [filters, contactSearch, startDateISO, endDateISO, sortOrder, reviewStatus],
  );
  const [query] = useAtom(queryAtom);

  const conversations: SentinelConversationListItem[] = useMemo(() => {
    if (!query.data?.pages) return [];
    return query.data.pages.flatMap((page) => page.conversations);
  }, [query.data]);

  const hasMore = query.data?.pages[query.data.pages.length - 1]?.hasMore ?? false;
  const totalCount = query.data?.pages[0]?.totalCount ?? 0;
  const isFetchingNextPage = query.isFetchingNextPage;

  const queryRef = useRef(query);
  queryRef.current = query;

  const fetchNextPage = useCallback(() => {
    if (hasMore && !isFetchingNextPage) {
      queryRef.current.fetchNextPage();
    }
  }, [hasMore, isFetchingNextPage]);

  return {
    data: conversations,
    totalCount,
    isLoading: query.isLoading,
    isError: query.isError,
    hasMore,
    isFetchingNextPage,
    fetchNextPage,
    refetch: query.refetch,
  };
}

/**
 * Cache of per-conversation detail atoms to prevent recreation on re-render.
 */
const sentinelDetailAtomsCache = new Map<number, ReturnType<typeof atomWithQuery>>();

const getSentinelDetailAtom = (conversationId: number) => {
  if (!sentinelDetailAtomsCache.has(conversationId)) {
    sentinelDetailAtomsCache.set(
      conversationId,
      atomWithQuery(() => ({
        // Unscoped on purpose: a conversation id already names one conversation and
        // therefore one brand, so the thread cannot differ between brands.
        queryKey: ["sentinel", "conversation", conversationId],
        queryFn: async () => sentinelReviewService.getConversationThread(conversationId),
        enabled: conversationId > 0,
        refetchOnWindowFocus: false,
        staleTime: STALE_TIME_MS,
      })),
    );
  }
  return sentinelDetailAtomsCache.get(conversationId)!;
};

/**
 * Hook: a single conversation's full thread (all turns + attached reviews).
 */
export function useSentinelConversation(conversationId?: number) {
  const atom = useMemo(() => {
    if (!conversationId || conversationId <= 0) {
      return atomWithQuery(() => ({
        queryKey: ["sentinel", "conversation", 0, "dummy"],
        queryFn: async () => null,
        enabled: false,
      }));
    }
    return getSentinelDetailAtom(conversationId);
  }, [conversationId]);

  const [query] = useAtom(atom);

  return {
    data: (query.data as SentinelConversationThread | null) ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}

/**
 * Hook: list submitted reviews for the feedback-viewer page.
 */
export function useSentinelReviews(filters: SentinelReviewsFilters) {
  const filterKey = JSON.stringify({
    verdict: filters.verdict ?? null,
    page: filters.page ?? 1,
    limit: filters.limit ?? 20,
  });

  const atom = useMemo(
    () =>
      atomWithQuery((get) => ({
        queryKey: ["sentinel", "reviews", filterKey, buScopeKey(get)],
        queryFn: async () => sentinelReviewService.getReviews(filters, buScopeParams(get)),
        refetchOnWindowFocus: false,
        staleTime: STALE_TIME_MS,
      })),
    [filterKey],
  );

  const [query] = useAtom(atom);
  const data = query.data as SentinelReviewsResponse | undefined;

  return {
    items: data?.items ?? [],
    total: data?.total ?? 0,
    page: data?.page ?? filters.page ?? 1,
    limit: data?.limit ?? filters.limit ?? 20,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}

/**
 * Hook: create / edit / delete a Sentinel review, plus the administrator's
 * response edits. Each call returns a boolean success flag and shares the
 * pending/error state.
 */
export function useSubmitSentinelReview() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const run = useCallback(async (action: () => Promise<unknown>, fallbackMessage: string): Promise<boolean> => {
    setIsSubmitting(true);
    setError(null);
    try {
      await action();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(fallbackMessage));
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const submit = useCallback(
    (input: SubmitSentinelReviewInput): Promise<boolean> =>
      run(() => sentinelReviewService.submitReview(input), "Failed to submit review"),
    [run],
  );

  const edit = useCallback(
    (reviewId: string, patch: EditSentinelReviewInput): Promise<boolean> =>
      run(() => sentinelReviewService.editReview(reviewId, patch), "Failed to update review"),
    [run],
  );

  const remove = useCallback(
    (reviewId: string): Promise<boolean> =>
      run(() => sentinelReviewService.deleteReview(reviewId), "Failed to delete review"),
    [run],
  );

  const setAddressed = useCallback(
    (reviewId: string, addressed: boolean): Promise<boolean> =>
      run(() => sentinelReviewService.setAddressed(reviewId, addressed), "Failed to update review"),
    [run],
  );

  // Administrator-only response management (the backend gates these on client-review.manage).
  const editResponse = useCallback(
    (sentinelMessageId: number, content: string): Promise<boolean> =>
      run(() => sentinelReviewService.updateResponse(sentinelMessageId, content), "Failed to update response"),
    [run],
  );

  const removeResponse = useCallback(
    (sentinelMessageId: number): Promise<boolean> =>
      run(() => sentinelReviewService.deleteResponse(sentinelMessageId), "Failed to delete response"),
    [run],
  );

  const removeConversation = useCallback(
    (conversationId: number): Promise<boolean> =>
      run(() => sentinelReviewService.deleteConversationResponses(conversationId), "Failed to delete conversation"),
    [run],
  );

  return {
    submit,
    edit,
    remove,
    setAddressed,
    editResponse,
    removeResponse,
    removeConversation,
    isSubmitting,
    error,
  };
}
